import { Block, PrintDocument, RenderOutput } from '../document'
import { ascii, row, wrap } from '../text'

const ESC = 0x1b
const GS = 0x1d
const LF = 0x0a

/** ESC/POS (Epson, Xprinter, Rongta, most thermal receipt printers). Roll documents; labels print as a short roll. */
export const renderEscPos = (doc: PrintDocument, p: Record<string, any>): RenderOutput => { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (doc.kind === 'sheet') throw new Error('A receipt printer cannot print a page of labels')
  const width = Number(p.chars_per_line)
  const out: number[] = [ESC, 0x40, ESC, 0x74, Number(p.code_page)]
  if (doc.kind === 'roll' && doc.drawer) out.push(ESC, 0x70, Number(p.drawer_pin), Math.round(p.drawer_on_ms / 2), Math.round(p.drawer_off_ms / 2))

  const line = (text: string) => out.push(...ascii(text), LF)
  const align = (a = 'left') => out.push(ESC, 0x61, a === 'center' ? 1 : a === 'right' ? 2 : 0)
  const bold = (on: boolean) => out.push(ESC, 0x45, on ? 1 : 0)

  const block = (b: Block) => {
    switch (b.type) {
      case 'text': {
        align(b.align)
        bold(!!b.bold)
        if (b.size === 2) out.push(GS, 0x21, 0x11)
        for (const l of wrap(b.text, b.size === 2 ? Math.floor(width / 2) : width)) line(l)
        if (b.size === 2) out.push(GS, 0x21, 0x00)
        bold(false)
        break
      }
      case 'row':
        align('left')
        bold(!!b.bold)
        line(row(b.left, b.right, width))
        bold(false)
        break
      case 'rule':
        align('left')
        line('-'.repeat(width))
        break
      case 'feed':
        out.push(ESC, 0x64, b.lines)
        break
      case 'barcode': {
        align(b.align ?? 'center')
        out.push(GS, 0x68, Number(p.barcode_height_dots), GS, 0x77, 2, GS, 0x48, b.show_text === false ? 0 : 2, GS, 0x66, 0)
        if (b.symbology === 'ean13') {
          const data = ascii(b.value.slice(0, 12))
          out.push(GS, 0x6b, 67, data.length, ...data)
        } else {
          const data = ascii(`{B${b.value}`)
          out.push(GS, 0x6b, 73, data.length, ...data)
        }
        out.push(LF)
        break
      }
      case 'qr': {
        align(b.align ?? 'center')
        if (!p.qr) {
          line(b.value)
          break
        }
        const data = ascii(b.value)
        const len = data.length + 3
        out.push(GS, 0x28, 0x6b, 4, 0, 0x31, 0x41, 0x32, 0x00) // model 2
        out.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x43, 6) // module size
        out.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x45, 0x31) // error correction M
        out.push(GS, 0x28, 0x6b, len & 0xff, len >> 8, 0x31, 0x50, 0x30, ...data)
        out.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x51, 0x30, LF)
        break
      }
      case 'image':
        // Logos need a raster conversion; not built yet.
        break
    }
  }

  doc.blocks.forEach(block)
  align('left')
  out.push(ESC, 0x64, 3)
  if (doc.kind === 'label' || doc.cut !== false) {
    if (p.cut === 'full') out.push(GS, 0x56, 0x41, 0x00)
    else if (p.cut === 'partial') out.push(GS, 0x56, 0x42, 0x00)
  }
  return { bytes: Uint8Array.from(out) }
}

/** The drawer kick alone, for a cash sale that prints nothing. */
export const escPosDrawerKick = (p: Record<string, any>) => // eslint-disable-line @typescript-eslint/no-explicit-any
  Uint8Array.from([ESC, 0x70, Number(p.drawer_pin), Math.round(p.drawer_on_ms / 2), Math.round(p.drawer_off_ms / 2)])
